<template>
	<div class="settings-view" :class="colourMode">
		<div class="settings-header" :style="{ paddingTop: (20 + safeTop) + 'px' }">
			<button class="settings-back" @click="$emit('close')">
				<img :src="require('@/assets/arrow-left.png')" alt="Back" class="back-arrow-icon" />
			</button>
			<h1 class="settings-title">Settings</h1>
		</div>
		<div class="settings-content">
			<div class="settings-nav">
				<button class="settings-nav-item" :class="{ active: activeTab === 'general' }" @click="activeTab = 'general'">General</button>
				<button class="settings-nav-item" :class="{ active: activeTab === 'account' }" @click="activeTab = 'account'">Account</button>
				<button class="settings-nav-item" :class="{ active: activeTab === 'privacy' }" @click="activeTab = 'privacy'">Privacy</button>
			</div>
			<div class="settings-panel">
				<!-- General Tab -->
				<template v-if="activeTab === 'general'">
				<!-- Profile Section -->
				<div class="settings-section">
					<h2 class="settings-section-title">Profile</h2>
					<div v-if="customer" class="profile-info">
						<div class="profile-row">
							<span class="profile-label">Name</span>
							<span class="profile-value">{{ customer.person?.firstName }} {{ customer.person?.lastName }}</span>
						</div>
						<div class="profile-row">
							<span class="profile-label">Email</span>
							<span class="profile-value">{{ customer.person?.email }}</span>
						</div>
						<div v-if="customer.person?.mobilePhone" class="profile-row">
							<span class="profile-label">Phone</span>
							<span class="profile-value">{{ customer.person?.mobilePhone }}</span>
						</div>
					</div>
					<div v-else class="settings-loading">Loading profile...</div>
				</div>

				<!-- Notifications Section -->
				<div class="settings-section">
					<h2 class="settings-section-title">Notifications</h2>
					<div v-if="customer?.preferences" class="notification-toggles">
						<div class="notification-toggle">
							<div class="toggle-info">
								<span class="toggle-label">Daily current balance</span>
								<span class="toggle-description">Receive your current account balance each morning</span>
							</div>
							<v-switch
								:model-value="customer.preferences.dailyCurrentBalanceNotifications"
								hide-details
								@update:model-value="$emit('update-pref', 'dailyCurrentBalanceNotifications', $event)"
							></v-switch>
						</div>
						<div class="notification-toggle">
							<div class="toggle-info">
								<span class="toggle-label">Daily credit balance</span>
								<span class="toggle-description">Receive your credit balance each morning</span>
							</div>
							<v-switch
								:model-value="customer.preferences.dailyCreditBalanceNotifications"
								hide-details
								@update:model-value="$emit('update-pref', 'dailyCreditBalanceNotifications', $event)"
							></v-switch>
						</div>
						<div class="notification-toggle">
							<div class="toggle-info">
								<span class="toggle-label">Quiz notifications</span>
								<span class="toggle-description">Get notified about new quizzes</span>
							</div>
							<v-switch
								:model-value="customer.preferences.quizNotifications"
								hide-details
								@update:model-value="$emit('update-pref', 'quizNotifications', $event)"
							></v-switch>
						</div>
					</div>
					<div v-else class="settings-loading">Loading preferences...</div>
				</div>

				<!-- Appearance Section -->
				<div class="settings-section">
					<h2 class="settings-section-title">Appearance</h2>
					<div class="settings-label">Colour mode</div>
					<div class="colour-mode-options">
						<button
							v-for="mode in colourModes"
							:key="mode.value"
							class="colour-mode-option"
							:class="{ active: colourMode === mode.value }"
							@click="$emit('select-colour-mode', mode.value)"
						>
							<div class="colour-mode-preview" :class="mode.value">
								<div class="preview-sidebar"></div>
								<div class="preview-content">
									<div class="preview-line short"></div>
									<div class="preview-line"></div>
									<div class="preview-input">
										<div class="preview-dot"></div>
									</div>
								</div>
							</div>
							<span class="colour-mode-label">{{ mode.label }}</span>
						</button>
					</div>
				</div>
				</template>

				<!-- Account Tab -->
				<div v-if="activeTab === 'account'" class="settings-placeholder">
					<p>Account settings coming soon.</p>
				</div>

				<!-- Privacy Tab -->
				<template v-if="activeTab === 'privacy'">
					<div class="settings-section">
						<h2 class="settings-section-title">Display</h2>
						<div class="notification-toggles">
							<div class="notification-toggle">
								<div class="toggle-info">
									<span class="toggle-label">Hide financial figures</span>
									<span class="toggle-description">Mask balances and amounts for privacy</span>
								</div>
								<v-switch
									v-model="hideFinancialFigures"
									hide-details
								></v-switch>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'SettingsOverlay',
	props: {
		customer: { type: Object, default: null },
		colourMode: { type: String, default: 'dark' },
		safeTop: { type: Number, default: 0 }
	},
	emits: ['close', 'update-pref', 'select-colour-mode'],
	data() {
		return {
			activeTab: 'general',
			colourModes: [
				{ value: 'light', label: 'Light' },
				{ value: 'dark', label: 'Dark' }
			],
			hideFinancialFigures: localStorage.getItem('randomiseFigures') === 'true'
		}
	},
	watch: {
		hideFinancialFigures(val) {
			localStorage.setItem('randomiseFigures', val);
			window.dispatchEvent(new CustomEvent('randomise-figures-changed', { detail: val }));
		}
	}
}
</script>

<style scoped>
.settings-view {
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
}

.settings-header {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 20px 24px;
}

.settings-back {
	width: 36px;
	height: 36px;
	background: transparent;
	border: none;
	border-radius: 8px;
	color: var(--dark-text-primary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
}

.settings-back:hover {
	background: var(--dark-bg-secondary);
}

.back-arrow-icon {
	width: 16px;
	height: 12px;
	filter: brightness(0) invert(1);
}

.settings-title {
	color: var(--dark-text-primary);
	font-size: 24px;
	margin: 0;
}

.settings-content {
	display: flex;
	flex: 1;
	padding: 0 24px 24px;
	gap: 40px;
}

.settings-nav {
	width: 180px;
	flex-shrink: 0;
}

.settings-nav-item {
	display: block;
	width: 100%;
	padding: 10px 16px;
	background: transparent;
	border: none;
	border-radius: 8px;
	color: var(--dark-text-primary);
	font-size: 15px;
	text-align: left;
	cursor: pointer;
}

.settings-nav-item:hover {
	background: var(--dark-bg-secondary);
}

.settings-nav-item.active {
	background: var(--dark-bg-secondary);
}

.settings-panel {
	flex: 1;
	max-width: 600px;
}

.settings-placeholder {
	color: var(--dark-text-secondary);
	font-size: 14px;
}

.settings-section {
	margin-bottom: 32px;
}

.settings-section-title {
	color: var(--dark-text-primary);
	font-size: 18px;
	margin: 0 0 16px 0;
}

.settings-label {
	color: var(--dark-text-secondary);
	font-size: 14px;
	margin-bottom: 12px;
}

.colour-mode-options {
	display: flex;
	gap: 16px;
}

.colour-mode-option {
	width: 120px;
	background: transparent;
	border: 2px solid transparent;
	border-radius: 12px;
	padding: 0;
	cursor: pointer;
	transition: border-color 0.2s;
}

.colour-mode-option.active {
	border-color: #6b9eff;
}

.colour-mode-preview {
	display: flex;
	border-radius: 10px;
	overflow: hidden;
	height: 80px;
}

.colour-mode-preview.light {
	background: var(--background-color-alt);
}

.colour-mode-preview.light .preview-sidebar {
	background: var(--border-color);
}

.colour-mode-preview.light .preview-line {
	background: var(--border-color);
}

.colour-mode-preview.light .preview-input {
	background: var(--border-color);
}

.colour-mode-preview.dark {
	background: #2f2f2f;
}

.colour-mode-preview.dark .preview-sidebar {
	background: #252525;
}

.colour-mode-preview.dark .preview-line {
	background: #555;
}

.colour-mode-preview.dark .preview-input {
	background: #404040;
}

.preview-sidebar {
	width: 30%;
}

.preview-content {
	flex: 1;
	padding: 10px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
}

.preview-line {
	height: 6px;
	border-radius: 3px;
}

.preview-line.short {
	width: 60%;
}

.preview-input {
	height: 16px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding-right: 4px;
}

.preview-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: var(--dark-accent);
}

.colour-mode-label {
	display: block;
	color: var(--dark-text-primary);
	font-size: 13px;
	padding: 8px 0;
}

.settings-loading {
	color: var(--dark-text-secondary);
	font-size: 14px;
}

/* Profile styles */
.profile-info {
	background: var(--dark-bg-secondary);
	border-radius: 12px;
	padding: 4px 0;
}

.profile-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 14px 16px;
	border-bottom: 1px solid var(--dark-border);
}

.profile-row:last-child {
	border-bottom: none;
}

.profile-label {
	color: var(--dark-text-secondary);
	font-size: 14px;
}

.profile-value {
	color: var(--dark-text-primary);
	font-size: 14px;
}

/* Notification toggle styles */
.notification-toggles {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.notification-toggle {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	background: var(--dark-bg-secondary);
	border-radius: 12px;
}

.toggle-info {
	flex: 1;
}

.toggle-label {
	display: block;
	color: var(--dark-text-primary);
	font-size: 15px;
	margin-bottom: 4px;
}

.toggle-description {
	display: block;
	color: var(--dark-text-secondary);
	font-size: 13px;
}

.notification-toggle :deep(.v-switch) {
	flex: none;
}

.notification-toggle :deep(.v-switch__track) {
	background: var(--dark-border);
	opacity: 1;
}

.notification-toggle :deep(.v-switch--inset .v-switch__track) {
	background: var(--dark-border);
}

.notification-toggle :deep(.v-selection-control--dirty .v-switch__track) {
	background: var(--dark-accent) !important;
}

.notification-toggle :deep(.v-selection-control--dirty .v-switch__thumb) {
	background: var(--dark-accent) !important;
}

/* Light Mode */
.settings-view.light .settings-back {
	color: var(--text-color);
}

.settings-view.light .settings-back:hover {
	background: var(--background-color-alt);
}

.settings-view.light .back-arrow-icon {
	filter: none;
}

.settings-view.light .settings-title {
	color: var(--text-color);
}

.settings-view.light .settings-nav-item {
	color: var(--text-color);
}

.settings-view.light .settings-nav-item:hover {
	background: var(--background-color-alt);
}

.settings-view.light .settings-nav-item.active {
	background: var(--border-color) !important;
	color: var(--text-color) !important;
}

.settings-view.light .settings-section-title {
	color: var(--text-color);
}

.settings-view.light .settings-label {
	color: var(--grey-text);
}

.settings-view.light .colour-mode-label {
	color: var(--text-color);
}

.settings-view.light .settings-loading {
	color: var(--grey-text);
}

.settings-view.light .settings-placeholder {
	color: var(--grey-text);
}

.settings-view.light .profile-info {
	background: var(--background-color);
	border: 1px solid var(--border-color);
}

.settings-view.light .profile-row {
	border-color: var(--background-color-alt);
}

.settings-view.light .profile-label {
	color: var(--grey-text);
}

.settings-view.light .profile-value {
	color: var(--text-color);
}

.settings-view.light .notification-toggle {
	background: var(--background-color);
	border: 1px solid var(--border-color);
}

.settings-view.light .toggle-label {
	color: var(--text-color);
}

.settings-view.light .toggle-description {
	color: var(--grey-text);
}

.settings-view.light .notification-toggle :deep(.v-switch__track) {
	background: var(--border-color);
}

/* Responsive: Settings on mobile */
@media (max-width: 767px) {
	.settings-header {
		padding: 16px;
	}

	.settings-back {
		width: 40px;
		height: 40px;
		background: var(--dark-bg-secondary);
	}

	.settings-view.light .settings-back {
		background: var(--background-color-alt);
	}

	.settings-nav {
		display: none;
	}

	.settings-content {
		padding: 0 16px 24px;
	}

	.colour-mode-options {
		flex-wrap: wrap;
	}

	.colour-mode-option {
		width: calc(50% - 8px);
	}
}
</style>
